import { faBellOn, faBellSlash } from '@fortawesome/pro-regular-svg-icons';
import {
    faBellOn as faBellOnSolid,
    faBellSlash as faBellSlashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
    HardwareHealthNotificationSubscriptionResponse,
    HardwareHealthNotificationSubscriptionUpdateRequest,
    HardwareHealthNotificationTriggerRequestChild,
    HardwareNotificationSubscriptionResponse,
    HardwareNotificationSubscriptionUpdateRequest,
    IndicatorNotificationSubscriptionResponse,
    IndicatorNotificationSubscriptionUpdateRequest,
    MachineHealthNotificationSubscriptionResponse,
    MachineHealthNotificationSubscriptionUpdateRequest,
    MachineHealthNotificationTriggerRequestChild,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { getNotificationTypeDisplayName } from 'pages/Settings/NotificationSubscriptions/shared';
import { useThemeValues } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';

type NotificationSubscriptionToggleButtonProps = {
    subscription: NotificationSubscription;
    onToggle: () => void;
};

type NotificationSubscriptionToggleResponse = Promise<
    | IndicatorNotificationSubscriptionResponse
    | HardwareNotificationSubscriptionResponse
    | MachineHealthNotificationSubscriptionResponse
    | HardwareHealthNotificationSubscriptionResponse
>;

export function NotificationSubscriptionToggleButton({
    subscription,
    onToggle,
}: NotificationSubscriptionToggleButtonProps) {
    const { showToast } = useNotificationContext();
    const { secondaryText } = useThemeValues();

    const isSilenced = subscription.IsSilenced;

    const { mutate: toggleNotificationSubscription, isPending: isToggling } = useMutation(
        {
            mutationFn: (): NotificationSubscriptionToggleResponse => {
                switch (subscription._discriminator) {
                    case 'Indicator':
                        return sdClient.updateIndicatorNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new IndicatorNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                UnacknowledgedPeriod: subscription.UnacknowledgedPeriod,
                                NotificationTriggers: subscription.NotificationTriggers,
                            }),
                        });
                    case 'Hardware':
                        return sdClient.updateHardwareNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new HardwareNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                NotificationTriggers: subscription.NotificationTriggers,
                            }),
                        });
                    case 'MachineHealth':
                        return sdClient.updateMachineHealthNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new MachineHealthNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                // TODO @jp: change this backend schema, ew
                                NotificationTriggers: subscription.NotificationTriggers.map(
                                    (nt) =>
                                        new MachineHealthNotificationTriggerRequestChild({
                                            NotificationTriggerId:
                                                nt.NotificationTriggerId,
                                            MachineHealthEventType:
                                                nt.MachineHealthEventType,
                                            IsSdConnectPushEnabled:
                                                nt.IsSdConnectPushEnabled,
                                            IsEmailEnabled: nt.IsEmailEnabled,
                                            IsSmsEnabled: nt.IsSmsEnabled,
                                        })
                                ),
                            }),
                        });
                    case 'HardwareHealth':
                        return sdClient.updateNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new HardwareHealthNotificationSubscriptionUpdateRequest(
                                {
                                    IsSilenced: !isSilenced,
                                    NotificationTriggers: subscription.NotificationTriggers.map(
                                        (nt) =>
                                            new HardwareHealthNotificationTriggerRequestChild(
                                                {
                                                    FaultTypeId: nt.FaultType.Id,
                                                    IsSdConnectPushEnabled:
                                                        nt.IsSdConnectPushEnabled,
                                                    IsEmailEnabled: nt.IsEmailEnabled,
                                                    IsSmsEnabled: nt.IsSmsEnabled,
                                                }
                                            )
                                    ),
                                }
                            ),
                        }) as Promise<HardwareHealthNotificationSubscriptionResponse>;
                    default:
                        throw new Error('invalid notification type');
                }
            },

            onSuccess: () => {
                onToggle();
                showToast({
                    content: `Successfully ${
                        isSilenced ? 'unsilenced' : 'silenced'
                    } ${getNotificationTypeDisplayName(
                        subscription
                    )} notification subscription`,
                    type: 'success',
                });
            },

            onError: () => {
                showToast({
                    content: `Failed to ${
                        isSilenced ? 'unsilence' : 'silence'
                    } ${getNotificationTypeDisplayName(
                        subscription
                    )} notification subscription`,
                    type: 'error',
                });
            },
        }
    );

    return (
        <HoverIconButton
            title={`${
                isSilenced ? 'Unsilence' : 'Silence'
            } ${getNotificationTypeDisplayName(subscription)} notification subscription`}
            icon={isSilenced ? faBellSlash : faBellOn}
            hoverIcon={isSilenced ? faBellOnSolid : faBellSlashSolid}
            color={secondaryText}
            hoverColor={KCFColor.blue5}
            onClick={() => toggleNotificationSubscription()}
            disabled={isToggling}
        />
    );
}
